var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import AlertPanelSideBarTemplate from '@front/templates/AlertPanelSideBarTemplate.html';
import { ControlsTypes } from '../../../UtilsClasses/FactoryConstants';
import { AlertSideBarController } from '@shared/commons/SideBarController/AlertSideBarController';
import { Resources } from '@shared/localizations/Resources';
import { HtmlScroll } from '@components/webdesktop/elements/Scroll/HtmlScroll';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { DataCache } from '@shared/commons/DataCache';
import { PanelSideBarBase } from './PanelSideBarBase';
import { PanelSettingsScreen } from '../../../screen/PanelSettingsScreen';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class AlertPanelSideBar extends PanelSideBarBase {
    constructor() {
        super();
        this._alertSideBarController = null;
        this._alertsArray = [];
        this.itemsContextMenu = [];
        this.applyProperties = (properties) => this.callBack(properties);
        this.onAddCreateAlertPanel = (alert) => __awaiter(this, void 0, void 0, function* () {
            yield this.set('alertForEditing', alert);
            yield this.set('createAlertPanelOpened', true);
        });
        this.onAddAlert = (alert) => {
            this._alertsArray.push(alert);
            this._alertsArray = this._alertSideBarController.sortAlerts(this._alertsArray);
            void this.set({
                alertsArray: this._alertsArray,
                canAddAlert: this.canAddRow()
            });
            this.fire('updateHeader');
        };
        this.onRemoveAlert = (alert) => {
            for (let i = 0; i < this._alertsArray.length; i++) {
                if (this._alertsArray[i].AlertId === alert.AlertId) {
                    this._alertsArray.splice(i, 1);
                }
            }
            this._alertsArray = this._alertSideBarController.sortAlerts(this._alertsArray);
            void this.set({
                alertsArray: this._alertsArray,
                canAddAlert: this.canAddRow()
            });
            this.fire('updateHeader');
        };
        this.onUpdateAlert = (alert) => {
            void this.update();
        };
    }
    oncomplete() {
        super.oncomplete();
        this._alertSideBarController = new AlertSideBarController();
        this.subscription();
        this.populateItems();
        this.populateContextMenu();
        void this.set({ canAddAlert: this.canAddRow() });
        this.addScroll();
        this.on('onAlertAction', this.onAlertAction);
        this.on('onAddClick', this.onAddClick);
        this.on('onCreateAlertClosed', this.onCreateAlertClosed);
    }
    onContextMenuOpen(event) {
        showContextMenu({ x: event.original.pageX, y: event.original.pageY, items: this.itemsContextMenu });
    }
    getType() {
        return ControlsTypes.AlertPanelSideBar;
    }
    onAlertAction(event, item, action) {
        const alert = item.Alert;
        this._alertSideBarController.onAlertActionButtonClick(alert, action);
    }
    localize() {
        void this.set({
            btnText: Resources.getResource('screen.Alerts.AddAlert'),
            btnTooltip: Resources.getResource('screen.Alerts.AddAlert.Tooltip'),
            btnDisabledTooltip: Resources.getResource('screen.Alerts.AddAlert.Disabled.Tooltip'),
            noDataText: Resources.getResource('screen.Alerts.noDataText')
        });
    }
    onteardown() {
        this._alertSideBarController.unsubscribe();
        this._alertSideBarController.unsubscribeToAdd(this.onAddAlert);
        this._alertSideBarController.unsubscribeToRemove(this.onRemoveAlert);
        this._alertSideBarController.unsubscribeToUpdate(this.onUpdateAlert);
        this._alertSideBarController.unsubscribeToEdit(this.onAddCreateAlertPanel);
    }
    canAddRow() {
        const arr = this.get('alertsArray');
        const count = DataCache.getRuleNumberValueForMyUser(RulesSet.VALUE_TOTAL_MAX_ALERTS);
        if (count === -1) {
            return true;
        }
        if (count <= arr.length) {
            return false;
        }
        return true;
    }
    isLostFocusForbidden() {
        return this.get('createAlertPanelOpened');
    }
    customLostFocus(parentPanel) {
        if (super.customLostFocus(parentPanel))
            return true;
        if (this.isLostFocusForbidden()) {
            const createAlertPanelRef = this.findComponent('CreateAlertPanel');
            if (createAlertPanelRef.checkPossibilityClosing()) {
                this.processFocus(null); // call automatically createAlertPanelRef?.lostFocus();
                void this.set('createAlertPanelOpened', false);
            }
            else {
                createAlertPanelRef.processFocus(null);
                createAlertPanelRef.afterSetFocus = () => {
                    parentPanel.setFocus();
                };
            }
            return true;
        }
        return false;
    }
    Properties() {
        return SessionSettings.GetAlertPanelProperties();
    }
    callBack(newProperties) {
        SessionSettings.AlertPanelCallBack(newProperties);
        SessionSettings.save();
    }
    onCreateAlertClosed() {
        void this.set('createAlertPanelOpened', false);
        void this.set('alertForEditing', null);
    }
    addScroll() {
        HtmlScroll.addScroll(this.find('.alertPanelSideBar-body'));
    }
    populateItems() {
        const arr = this._alertSideBarController.getAlerts();
        this._alertsArray = arr;
        void this.set({
            alertsArray: arr,
            canAddAlert: this.canAddRow()
        });
    }
    onAddClick() {
        void this.onAddCreateAlertPanel();
    }
    subscription() {
        this._alertSideBarController.subscribe();
        this._alertSideBarController.subscribeToAdd(this.onAddAlert);
        this._alertSideBarController.subscribeToRemove(this.onRemoveAlert);
        this._alertSideBarController.subscribeToUpdate(this.onUpdateAlert);
        this._alertSideBarController.subscribeToEdit(this.onAddCreateAlertPanel);
    }
    populateContextMenu() {
        this.itemsContextMenu = [
            {
                type: NewContextMenuItemType.Default,
                text: 'screen.Alerts.Settings.ContextMenu',
                svgImage: 'menuSettings',
                event: () => {
                    void PanelSettingsScreen.EditProperties(this, null, Resources.getResource('screen.Alerts.Settings.Screen'));
                }
            }
        ];
    }
}
PanelSideBarBase.extendWith(AlertPanelSideBar, {
    template: AlertPanelSideBarTemplate,
    data: function () {
        return {
            alertsArray: [],
            btnText: '',
            btnTooltip: '',
            btnDisabledTooltip: '',
            canAddAlert: true,
            noDataImg: 'alerts',
            createAlertPanelOpened: false,
            alertForEditing: null
        };
    }
});
